import { API_ENDPOINTS } from '../config/api';
import { robustFetch } from '../utils/apiUtils';

export const PODCAST_PROGRESS_STORAGE_KEY = 'podcastEpisodeProgressMapV1';

const clamp = (val: number, min: number, max: number) => Math.min(max, Math.max(min, val));

const normalizeKey = (value: any): string => String(value || '').trim().toLowerCase();

const getAuthToken = (): string => {
    try {
        return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    } catch (_) {
        return '';
    }
};

export const getPodcastProgressKey = (podcast: any): string => {
    if (!podcast) return '';
    return normalizeKey(podcast.slug || podcast._id || podcast.audioFile || podcast.title || '');
};

export const isSamePodcast = (a: any, b: any): boolean => {
    const keyA = getPodcastProgressKey(a);
    const keyB = getPodcastProgressKey(b);
    return !!keyA && !!keyB && keyA === keyB;
};

// Cross-subdomain cookie helpers for podcast progress
function setCrossSubdomainProgress(val: string) {
    if (typeof document === 'undefined') return;
    try {
        const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        const domainStr = isLocal ? '' : '; domain=.harivishnu.in';
        document.cookie = `${PODCAST_PROGRESS_STORAGE_KEY}=${encodeURIComponent(val)}; path=/${domainStr}; max-age=31536000; SameSite=Lax`;
    } catch (_) {}
}

function getCrossSubdomainProgress(): string | null {
    if (typeof document === 'undefined') return null;
    try {
        const match = document.cookie.match(new RegExp('(^|;\\s*)' + PODCAST_PROGRESS_STORAGE_KEY + '=([^;]*)'));
        return match ? decodeURIComponent(match[2]) : null;
    } catch (_) {
        return null;
    }
}

export const readPodcastProgressMap = (): Record<string, any> => {
    try {
        let raw = localStorage.getItem(PODCAST_PROGRESS_STORAGE_KEY);
        if (!raw) {
            raw = getCrossSubdomainProgress();
        }
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
        return {};
    }
};

export const writePodcastProgressMap = (map: Record<string, any>) => {
    try {
        const json = JSON.stringify(map || {});
        localStorage.setItem(PODCAST_PROGRESS_STORAGE_KEY, json);
        setCrossSubdomainProgress(json);
    } catch (_) {
        // Ignore storage failures
    }
};

export const getPodcastProgress = (podcast: any) => {
    const key = getPodcastProgressKey(podcast);
    if (!key) return null;
    const map = readPodcastProgressMap();
    return map[key] || null;
};

export const upsertPodcastProgress = (podcast: any, payload: { currentTime?: number; duration?: number; isPlaying?: boolean }) => {
    const key = getPodcastProgressKey(podcast);
    if (!key) return null;

    const map = readPodcastProgressMap();
    const prev = map[key] || {};

    const duration = Number.isFinite(payload.duration as number) ? Number(payload.duration) : Number(prev.duration || 0);
    const currentTimeRaw = Number.isFinite(payload.currentTime as number) ? Number(payload.currentTime) : Number(prev.currentTime || 0);
    const currentTime = clamp(currentTimeRaw, 0, Math.max(duration || 0, currentTimeRaw));
    const completed = duration > 0 && currentTime >= Math.max(duration - 3, duration * 0.97);

    const next = {
        key,
        currentTime: completed ? duration : currentTime,
        duration,
        progressPct: duration > 0 ? clamp(((completed ? duration : currentTime) / duration) * 100, 0, 100) : 0,
        started: completed ? true : ((completed || currentTime > 1 || prev.started) ? true : false),
        completed,
        isPlaying: !!payload.isPlaying,
        updatedAt: Date.now(),
    };

    map[key] = next;
    writePodcastProgressMap(map);
    return next;
};

export const syncPodcastProgressToServer = async (podcast: any, progressEntry?: any) => {
    const token = getAuthToken();
    if (!token || !podcast) return;

    const entry = progressEntry || getPodcastProgress(podcast);
    if (!entry) return;

    try {
        await robustFetch(API_ENDPOINTS.PODCAST.PROGRESS, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                podcastKey: getPodcastProgressKey(podcast),
                podcastId: podcast?._id || null,
                podcastSlug: podcast?.slug || null,
                podcastTitle: podcast?.title || null,
                ...entry,
            }),
        });
    } catch (_) {
        // Keep local progress as source of truth if server sync fails.
    }
};

export const hydratePodcastProgressFromServer = async (): Promise<Record<string, any>> => {
    const token = getAuthToken();
    if (!token) return readPodcastProgressMap();

    try {
        const res = await robustFetch(API_ENDPOINTS.PODCAST.PROGRESS, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!res.ok) return readPodcastProgressMap();

        const data = await res.json();
        const remoteMap = data && data.map && typeof data.map === 'object' ? data.map : {};
        const localMap = readPodcastProgressMap();

        const merged: Record<string, any> = { ...localMap };
        Object.keys(remoteMap).forEach((key) => {
            const localItem = localMap[key];
            const remoteItem = remoteMap[key];
            if (!localItem || (remoteItem?.updatedAt || 0) > (localItem?.updatedAt || 0)) {
                merged[key] = remoteItem;
            }
        });

        writePodcastProgressMap(merged);
        return merged;
    } catch (_) {
        return readPodcastProgressMap();
    }
};

export const formatSecondsCompact = (seconds: number): string => {
    const safe = Math.max(0, Math.floor(seconds || 0));
    const m = Math.floor(safe / 60);
    const s = safe % 60;
    if (m <= 0) return `${s}s`;
    return `${m}m`;
};
